import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, SortingState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import { useEffect, useMemo, useRef } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { BASE_SORTING_TABLE_FEATURE_PROPS } from "@/components/Tables/Aeros/shared"
import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import type { CompanySortField } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import {
  type SortableAccessorKey,
  accessorKeyToCompanySortField,
  companySortFieldToAccessoryKey,
  useCompanyColumns,
} from "@/screens/Companies/components/tables/companies.const"
import { useCompanySearchStore } from "@/screens/Companies/stores/useCompanySearchStore"
import type { CompanySearchFilters } from "@/screens/Companies/stores/useCompanySearchStore"
import { COMPANY_EXPLICIT_EMPTY_FILTER_KEYS } from "@/screens/Companies/types/searchParams"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"
import useDebounce from "@/utils/useDebounce"

export const CompaniesTable: FC = () => {
  const navigate = useNavigate()
  const currentSearch = useSearch({ from: "/app/companies/" })
  const { category, query, field: searchField, order, ...restSearchParams } = currentSearch

  const data = useCompanySearchStore(({ data }) => data)
  const fetching = useCompanySearchStore(({ fetching }) => fetching)
  const error = useCompanySearchStore(({ error }) => error)
  const executeSearch = useCompanySearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useCompanySearchStore(({ loadMore }) => loadMore)
  const clearStore = useCompanySearchStore(({ clearStore }) => clearStore)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const debouncedExecuteSearch = useDebounce((filters: CompanySearchFilters) => {
    executeSearch(queryClient, filters)
  }, DEBOUNCE_DELAY_MS)

  useEffect(() => {
    const searchFilters: CompanySearchFilters = {
      ...restSearchParams,
      query,
      category,
      field: searchField as CompanySortField | undefined,
      order,
    }
    debouncedExecuteSearch(searchFilters)
  }, [debouncedExecuteSearch, query, category, searchField, order, restSearchParams])

  const sorting = useMemo(() => {
    if (!searchField) return []

    const tableSortField = companySortFieldToAccessoryKey.get(searchField as CompanySortField)
    if (!tableSortField) return []

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [searchField, order])

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)
    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id as SortableAccessorKey
      const field = accessorKeyToCompanySortField.get(key)
      const params = {
        ...restSearchParams,
        query,
        category,
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }
      const refinedParams = getRefinedSearchParams(params, undefined, {
        preserveEmptyArrayKeys: COMPANY_EXPLICIT_EMPTY_FILTER_KEYS,
      })
      void navigate({
        search: refinedParams.search,
        to: "/app/companies",
        replace: true,
      })
    }
  }

  const [throttledLoadMore] = useThrottle(() => {
    const searchFilters: CompanySearchFilters = {
      ...restSearchParams,
      query,
      category,
      field: searchField as CompanySortField | undefined,
      order,
    }
    loadMore(queryClient, searchFilters)
  }, 200)

  const scrollHandler = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const onRowClick = (row: { original: { companyId: string } }) =>
    void navigate({ from: "/app/companies", to: `${row.original.companyId}/edit` })

  const columns = useCompanyColumns()

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  return (
    <AerosBaseTable
      error={error}
      features={{
        table: BASE_SORTING_TABLE_FEATURE_PROPS.table,
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: fetching,
          },
          ...BASE_SORTING_TABLE_FEATURE_PROPS.tanstackOptions,
          onSortingChange: handleSortingChange,
          getCoreRowModel: getCoreRowModel(),
        },
      }}
      slotProps={{
        container: {
          ref: tableContainerRef,
          onScroll: scrollHandler,
        },
        bodyRow: {
          className: "hover:bg-primary-100 cursor-pointer",
          onClick: (row) => {
            if ("original" in row) {
              onRowClick(row)
            }
          },
        },
      }}
      noDataMessage='No companies found'
    />
  )
}

export default CompaniesTable
